import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  CheckCircle2,
  XCircle,
  ArrowLeft,
  RotateCcw,
  Play,
  Repeat2,
  Save,
  Loader2,
  Bookmark,
  Mic,
  Square,
  AlertTriangle,
} from "lucide-react";
import { toast } from "sonner";
import { AppShell, PilotDisclaimer } from "@/components/AppShell";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import {
  getScenario,
  getSession,
  finishSession,
  speak,
  toggleSaveSession,
  retryTurn,
  addPhrase,
} from "@/lib/api";
import { canRecord, useVoiceInput } from "@/hooks/useVoiceInput";

export default function Feedback() {
  const { sessionId } = useParams();
  const { lang, tr } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [session, setSession] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [reloadNonce, setReloadNonce] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        let s = await getSession(sessionId);
        if (!s.feedback) {
          // Finish is idempotent server-side; if the /finish just before
          // navigation didn't persist feedback yet, ask again. Surface any
          // failure as a recoverable error state (not a silent blank).
          try {
            const res = await finishSession(sessionId);
            if (res) {
              s = res.session;
              s.feedback = res.feedback;
            }
          } catch (finishErr) {
            if (cancelled) return;
            setLoadError(finishErr?.response?.data?.detail || tr("errorGeneric"));
            return;
          }
        }
        if (cancelled) return;
        setSession(s);
        try {
          const sc = await getScenario(s.scenario_id);
          if (!cancelled) setScenario(sc);
        } catch (_scErr) {
          // Scenario metadata is optional for the review UI (labels fall
          // back to ids); don't fail the whole screen if it 404s.
        }
      } catch (err) {
        if (cancelled) return;
        setLoadError(err?.response?.data?.detail || tr("errorGeneric"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId, reloadNonce]);

  const learnerTurns = useMemo(
    () => (session?.turns || []).map((t, i) => ({ ...t, __i: i })).filter((t) => t.role === "learner"),
    [session]
  );

  const playText = async (text) => {
    try {
      const blob = await speak(text, 1.0);
      const url = URL.createObjectURL(blob);
      const audio = new Audio(url);
      audio.onended = () => URL.revokeObjectURL(url);
      await audio.play();
    } catch (err) {
      toast.info(tr("ttsUnavailable"));
    }
  };

  const findCheckpointLabel = (id) => {
    if (!scenario) return id;
    const cp = scenario.checkpoints.find((c) => c.id === id);
    if (!cp) return id;
    return lang === "en" ? cp.en : cp.es;
  };

  const doToggleSave = async () => {
    if (!user) return;
    setSaving(true);
    try {
      const next = !session.saved;
      const r = await toggleSaveSession(sessionId, next);
      setSession({ ...session, saved: r.saved });
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  // Leave-safety guard (compatible with legacy <BrowserRouter>).
  // Any Back/Home/CTA that would leave Feedback while the practice is
  // finished-but-unsaved must first open the decision modal. Once saved
  // (or if the user picks "Salir sin guardar" / "Guardar en Mi progreso"),
  // the queued navigation runs. Refresh/close triggers a native
  // beforeunload confirmation.
  const [pendingNav, setPendingNav] = useState(null);
  const isUnsaved = !!session && !session.saved && !!user;

  const guardedNavigate = (dest) => {
    if (isUnsaved) {
      setPendingNav(() => () => navigate(dest));
      return;
    }
    navigate(dest);
  };

  useEffect(() => {
    if (!isUnsaved) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    // Global click-capture: any <a> or <button> that would navigate away
    // (bottom-nav Home/Practice/Phrases/Progress/Settings, brand link, or
    // any Link-driven destination outside /feedback/) is intercepted while
    // the practice is finished-but-unsaved so the decision modal always
    // shows — matches the guardedNavigate rule for in-page CTAs.
    const onCaptureClick = (e) => {
      // Sign Out is a <button>, not an <a href>; intercept it by testid
      // so the unsaved-review decision modal runs before we log the user
      // out. Save-and-continue then runs `logout(); navigate("/")`.
      const signoutBtn = e.target.closest('[data-testid="signout-btn"]');
      if (signoutBtn) {
        e.preventDefault();
        e.stopPropagation();
        setPendingNav(() => () => { logout(); navigate("/"); });
        return;
      }
      const el = e.target.closest("a[href]");
      if (!el) return;
      const href = el.getAttribute("href") || "";
      if (!href.startsWith("/")) return;
      if (href.startsWith(`/feedback/${sessionId}`)) return;
      e.preventDefault();
      e.stopPropagation();
      setPendingNav(() => () => navigate(href));
    };
    document.addEventListener("click", onCaptureClick, true);
    return () => {
      window.removeEventListener("beforeunload", onBeforeUnload);
      document.removeEventListener("click", onCaptureClick, true);
    };
  }, [isUnsaved, sessionId, navigate, logout]);

  const handleSaveFromPrompt = async () => {
    if (!user || saving) return;
    setSaving(true);
    try {
      const r = await toggleSaveSession(sessionId, true);
      setSession({ ...session, saved: r.saved });
      toast.success(tr("unsavedSaveCta"));
      const go = pendingNav;
      setPendingNav(null);
      if (go) go();
    } catch (err) {
      toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setSaving(false);
    }
  };

  const handleExitWithoutSave = () => {
    const go = pendingNav;
    setPendingNav(null);
    if (go) go();
  };

  const handleStayAndReview = () => setPendingNav(null);

  if (loading) {
    return (
      <AppShell hideNav={!user}>
        <section
          className="px-5 pt-10 pb-10 flex flex-col items-center justify-center gap-3 text-center"
          data-testid="feedback-loading"
        >
          <Loader2 size={28} className="animate-spin text-[rgb(var(--wpe-teal))]" />
          <p className="text-[13px] text-[rgb(var(--wpe-slate))]" data-testid="feedback-loading-label">
            {tr("feedbackLoading")}
          </p>
        </section>
      </AppShell>
    );
  }
  if (loadError || !session) {
    return (
      <AppShell hideNav={!user}>
        <section
          className="px-5 pt-8 pb-10 space-y-4"
          data-testid="feedback-error-screen"
        >
          <div className="wpe-card space-y-3 border border-[rgb(var(--wpe-border))]">
            <div className="flex items-start gap-2">
              <AlertTriangle size={18} className="text-[rgb(var(--wpe-amber))] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <h2 className="text-[15px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="feedback-error-title">
                  {tr("feedbackLoadErrorTitle")}
                </h2>
                <p className="text-[12px] text-[rgb(var(--wpe-slate))] mt-1" data-testid="feedback-error-detail">
                  {loadError || tr("errorGeneric")}
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-2 pt-1">
              <button
                type="button"
                onClick={() => setReloadNonce((n) => n + 1)}
                className="wpe-cta w-full"
                data-testid="feedback-retry-btn"
              >
                <RotateCcw size={14} /> {tr("feedbackLoadRetry")}
              </button>
              <button
                type="button"
                onClick={() => navigate(user ? "/home" : "/")}
                className="wpe-secondary w-full"
                data-testid="feedback-error-back-btn"
              >
                {tr("backHome")}
              </button>
            </div>
          </div>
        </section>
      </AppShell>
    );
  }
  const fb = session.feedback || {};

  return (
    <AppShell hideNav={!user}>
      <section className="px-5 pt-4 pb-10 space-y-5" data-testid="feedback-screen">
        <button
          type="button"
          onClick={() => guardedNavigate(user ? "/dashboard" : "/")}
          className="wpe-ghost text-[13px] -ml-2"
          data-testid="feedback-back-btn"
        >
          <ArrowLeft size={16} /> {tr("backHome")}
        </button>

        <div className="space-y-1">
          <h1 className="text-[24px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="feedback-heading">
            {tr("feedbackHowItWent")}
          </h1>
          <p className="text-[13px] text-[rgb(var(--wpe-slate))]" data-testid="feedback-subtitle">
            {tr("feedbackReviewSubtitle")}
          </p>
          <div
            className="text-[12px] font-semibold text-[rgb(var(--wpe-muted))] pt-1"
            data-testid="feedback-title"
          >
            {scenario && (lang === "en" ? scenario.title_en : scenario.title_es)}
          </div>
        </div>

        {fb.invalid_model_output && (
          <div className="wpe-hint text-[12px]" data-testid="feedback-invalid-note">
            {fb.no_learner_turns ? tr("noPartialFeedback") : tr("invalidFeedback")}
          </div>
        )}

        {fb.partial && !fb.invalid_model_output && (
          <div className="wpe-hint text-[12px]" data-testid="feedback-partial-note">
            {tr("partialFeedback")}
          </div>
        )}

        {user && (
          <div className="wpe-card flex items-start justify-between gap-3" data-testid="save-toggle-card">
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-medium text-[rgb(var(--wpe-navy))]">
                {session.saved ? tr("saveOff") : tr("saveToHistory")}
              </div>
              <div className="text-[11px] text-[rgb(var(--wpe-muted))] mt-0.5">
                {tr("saveNotice")}
              </div>
            </div>
            <button
              type="button"
              onClick={doToggleSave}
              disabled={saving}
              className={session.saved ? "wpe-secondary" : "wpe-cta"}
              data-testid="save-session-btn"
            >
              {saving ? (
                <Loader2 size={14} className="animate-spin" />
              ) : (
                <Save size={14} />
              )}
              {session.saved ? tr("saved") : tr("saveToHistory")}
            </button>
          </div>
        )}

        <div className="wpe-card space-y-2" data-testid="checkpoints-block">
          <h2 className="wpe-eyebrow">{tr("checkpointsList")}</h2>
          <ul className="space-y-2">
            {(fb.checkpoints || []).map((cp, i) => (
              <li key={i} className="flex items-start gap-2 text-[13px]">
                {cp.done ? (
                  <CheckCircle2 size={16} className="text-[rgb(var(--wpe-teal))] mt-0.5 shrink-0" />
                ) : (
                  <XCircle size={16} className="text-[rgb(var(--wpe-muted))] mt-0.5 shrink-0" />
                )}
                <div>
                  <div className="font-medium text-[rgb(var(--wpe-navy))]">
                    {findCheckpointLabel(cp.id)}
                  </div>
                  {cp.evidence && (
                    <div className="text-[12px] text-[rgb(var(--wpe-muted))] italic">
                      "{cp.evidence}"
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        <div className="wpe-card wpe-card-mint space-y-2" data-testid="done-well-block">
          <h2 className="wpe-eyebrow-teal">{tr("feedbackWhatWorked")}</h2>
          <ul className="space-y-1.5">
            {(fb.done_well || []).map((d, i) => (
              <li key={i} className="text-[13px] text-[rgb(var(--wpe-navy))] flex gap-2">
                <span className="text-[rgb(var(--wpe-teal))] mt-0.5">✓</span> {d}
              </li>
            ))}
          </ul>
        </div>

        <div className="wpe-card wpe-card-blue space-y-3" data-testid="improvements-block">
          <h2 className="wpe-eyebrow-blue">{tr("feedbackMoreNatural")}</h2>
          {(fb.improvements || []).length === 0 && (
            <p className="text-[13px] text-[rgb(var(--wpe-muted))]">—</p>
          )}
          {(fb.improvements || []).map((imp, i) => (
            <ImprovementCard
              key={i}
              index={i}
              imp={imp}
              sessionId={sessionId}
              learnerTurns={learnerTurns}
              tr={tr}
              playText={playText}
            />
          ))}
        </div>

        {fb.model_response_en && (
          <div className="wpe-card space-y-2" data-testid="model-response-block">
            <h2 className="wpe-eyebrow">{tr("modelResponse")}</h2>
            <div className="text-[14px] text-[rgb(var(--wpe-navy))]">{fb.model_response_en}</div>
            <button
              type="button"
              onClick={() => playText(fb.model_response_en)}
              className="wpe-ghost text-[12px]"
              data-testid="play-model-btn"
            >
              <Play size={13} /> {tr("listen")}
            </button>
          </div>
        )}

        {fb.encouragement_es && lang === "es" && !fb.no_learner_turns && !fb.invalid_model_output && (
          <div className="text-[13px] text-[rgb(var(--wpe-slate))] italic px-1" data-testid="encouragement">
            {fb.encouragement_es}
          </div>
        )}

        <PilotDisclaimer />

        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={() => guardedNavigate(`/briefing/${session.scenario_id}`)}
            className="wpe-cta wpe-cta-coral w-full"
            data-testid="retry-scenario-btn"
          >
            <RotateCcw size={14} /> {tr("feedbackRehearseAgain")}
          </button>
          <button
            type="button"
            onClick={() => guardedNavigate("/practice")}
            className="wpe-secondary w-full"
            data-testid="feedback-choose-another-btn"
          >
            {tr("feedbackChooseAnother")}
          </button>
          <button
            type="button"
            onClick={() => guardedNavigate("/create")}
            className="wpe-secondary w-full"
            data-testid="feedback-create-another-btn"
          >
            {tr("feedbackCreateAnother")}
          </button>
          <button
            type="button"
            onClick={() => guardedNavigate(user ? "/home" : "/")}
            className="wpe-ghost w-full"
            data-testid="back-dashboard-btn"
          >
            {tr("backHome")}
          </button>
        </div>

        {pendingNav && (
          <div
            className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-4"
            role="dialog"
            aria-modal="true"
            data-testid="unsaved-prompt"
          >
            <div className="bg-white rounded-2xl w-full max-w-sm p-5 space-y-3 shadow-xl">
              <h3 className="text-[16px] font-bold text-[rgb(var(--wpe-navy))]" data-testid="unsaved-prompt-title">
                {tr("unsavedTitle")}
              </h3>
              <p className="text-[13px] text-[rgb(var(--wpe-slate))]">
                {tr("unsavedBody")}
              </p>
              <div className="flex flex-col gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleSaveFromPrompt}
                  disabled={saving}
                  className="wpe-cta w-full"
                  data-testid="unsaved-save-btn"
                >
                  {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                  {tr("unsavedSaveCta")}
                </button>
                <button
                  type="button"
                  onClick={handleStayAndReview}
                  className="wpe-secondary w-full"
                  data-testid="unsaved-review-btn"
                >
                  {tr("unsavedReviewCta")}
                </button>
                <button
                  type="button"
                  onClick={handleExitWithoutSave}
                  className="wpe-ghost w-full text-[rgb(var(--wpe-muted))]"
                  data-testid="unsaved-exit-btn"
                >
                  {tr("unsavedExitCta")}
                </button>
              </div>
            </div>
          </div>
        )}
      </section>
    </AppShell>
  );
}

const CATEGORY_STYLES = {
  error: {
    // Warm coral — clearly a correction but never harsh red.
    wrapper: "border-[rgb(var(--wpe-coral))]/40 bg-[rgb(var(--wpe-coral))]/5",
    badge: "bg-[rgb(var(--wpe-coral))]/15 text-[rgb(var(--wpe-coral))]",
  },
  missing_objective: {
    wrapper: "border-[rgb(var(--wpe-gold))]/40 bg-[rgb(var(--wpe-gold))]/5",
    badge: "bg-[rgb(var(--wpe-gold))]/20 text-[rgb(var(--wpe-navy))]",
  },
  acceptable: {
    // Positive / affirming.
    wrapper: "border-[rgb(var(--wpe-teal))]/40 bg-[rgb(var(--wpe-teal-subtle))]",
    badge: "bg-[rgb(var(--wpe-teal))]/15 text-[rgb(var(--wpe-teal-hover))]",
  },
  optional_alternative: {
    // Muted / neutral — never framed as an error.
    wrapper: "border-[rgb(var(--wpe-border))] bg-white",
    badge: "bg-[rgb(var(--wpe-muted))]/10 text-[rgb(var(--wpe-slate))]",
  },
};

const ImprovementCard = ({ index, imp, sessionId, learnerTurns, tr, playText }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState(imp.improved || "");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [audioUsed, setAudioUsed] = useState(false);
  const voice = useVoiceInput({
    onTranscript: (t) => { setText((prev) => (prev ? prev + " " : "") + t); setAudioUsed(true); },
    onError: (kind) => {
      if (kind === "denied") toast.error(tr("micDenied"));
      else if (kind === "unsupported") toast.info(tr("micUnsupported"));
      else if (kind === "provider") toast.error(tr("errorGeneric"));
    },
  });

  const matchedTurn = useMemo(() => {
    const original = (imp.original || "").trim().toLowerCase();
    if (!original) return learnerTurns.at(-1);
    return (
      learnerTurns.find((t) => t.text.trim().toLowerCase() === original) ||
      learnerTurns.find((t) => t.text.trim().toLowerCase().includes(original)) ||
      learnerTurns.at(-1)
    );
  }, [imp, learnerTurns]);

  const submit = async () => {
    if (!matchedTurn) return;
    const trimmed = text.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      const r = await retryTurn(sessionId, matchedTurn.__i, trimmed, audioUsed);
      setResult(r);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 400) toast.error(tr("retryLimit"));
      else toast.error(err?.response?.data?.detail || tr("errorGeneric"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className={`rounded-lg border p-3 space-y-1 ${(CATEGORY_STYLES[imp.category] || CATEGORY_STYLES.optional_alternative).wrapper}`}
      data-testid={`improvement-${index}`}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] uppercase tracking-wider text-[rgb(var(--wpe-muted))]">
          {imp.original ? tr("original") : tr("missingLabel")}
        </div>
        <span
          className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${(CATEGORY_STYLES[imp.category] || CATEGORY_STYLES.optional_alternative).badge}`}
          data-testid={`improvement-category-${index}`}
        >
          {tr(`fbCat_${imp.category || "optional_alternative"}`)}
        </span>
      </div>
      {imp.original ? (
        <div className="text-[13px] text-[rgb(var(--wpe-slate))] italic">"{imp.original}"</div>
      ) : (
        <div className="text-[13px] text-[rgb(var(--wpe-muted))]">{tr("missingHint")}</div>
      )}
      <div className="text-[11px] uppercase tracking-wider text-[rgb(var(--wpe-muted))] mt-1">
        {imp.category === "acceptable"
          ? tr("fbCatAffirmLabel")
          : imp.category === "optional_alternative"
            ? tr("fbCatAlternativeLabel")
            : imp.original
              ? tr("improved")
              : tr("suggestionLabel")}
      </div>
      <div className="text-[13px] text-[rgb(var(--wpe-navy))] font-medium">{imp.improved}</div>
      <div className="flex items-center justify-between mt-1 flex-wrap gap-2">
        <div className="text-[12px] text-[rgb(var(--wpe-slate))]">{imp.explanation_es}</div>
        <div className="flex gap-2">
          {imp.improved && (
            <button
              type="button"
              onClick={() => playText(imp.improved)}
              className="wpe-ghost text-[11px]"
              data-testid={`play-improved-${index}`}
            >
              <Play size={12} /> {tr("listen")}
            </button>
          )}
          {imp.improved && (
            <button
              type="button"
              onClick={async () => {
                try {
                  await addPhrase({
                    english: imp.improved,
                    spanish: imp.explanation_es || "",
                    translation_es: imp.translation_es || "",
                    source_type: "correction",
                    source_session_id: sessionId,
                  });
                  toast.success(tr("savedPhrase"));
                } catch (err) {
                  toast.error(tr("errorGeneric"));
                }
              }}
              className="wpe-ghost text-[11px]"
              data-testid={`save-phrase-${index}`}
            >
              <Bookmark size={12} /> {tr("savePhraseCta")}
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="wpe-ghost text-[11px]"
            data-testid={`retry-turn-toggle-${index}`}
          >
            <Repeat2 size={12} /> {tr("retryThisReply")}
          </button>
        </div>
      </div>

      {open && (
        <div className="mt-2 space-y-2 border-t border-[rgb(var(--wpe-border))] pt-2" data-testid={`retry-panel-${index}`}>
          <div className="text-[11px] text-[rgb(var(--wpe-muted))]">{tr("retryHelp")}</div>
          <textarea
            className="wpe-input min-h-[70px] resize-none text-[13px]"
            placeholder={tr("retryPlaceholder")}
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, 1500))}
            data-testid={`retry-input-${index}`}
          />
          {canRecord() && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={voice.recording ? voice.stop : voice.start}
                disabled={voice.transcribing}
                className={`inline-flex items-center gap-1 px-3 py-2 rounded-full text-[12px] font-semibold ${voice.recording ? "bg-[rgb(var(--wpe-danger))] text-white" : "bg-[rgb(var(--wpe-teal-subtle))] text-[rgb(var(--wpe-teal-hover))]"}`}
                data-testid={`retry-mic-${index}`}
              >
                {voice.recording ? <Square size={12} /> : <Mic size={12} />}
                {voice.recording ? tr("stop") : (voice.transcribing ? tr("transcribing") : tr("micTap"))}
              </button>
              <span className="text-[10px] text-[rgb(var(--wpe-muted))]">
                {tr("reviewTranscript")}
              </span>
            </div>
          )}
          <button
            type="button"
            onClick={submit}
            disabled={submitting || !text.trim()}
            className="wpe-cta w-full text-[13px]"
            data-testid={`retry-submit-${index}`}
          >
            {submitting ? <Loader2 size={12} className="animate-spin" /> : <Repeat2 size={12} />}
            {tr("retrySubmit")}
          </button>

          {result && (
            <div className="space-y-2 mt-1" data-testid={`retry-result-${index}`}>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[rgb(var(--wpe-muted))]">
                  {tr("retryNew")}
                </div>
                <div className="text-[13px] text-[rgb(var(--wpe-navy))]">{result.new_learner}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[rgb(var(--wpe-muted))]">
                  {tr("retryCustomerReply")}
                </div>
                <div className="text-[13px] text-[rgb(var(--wpe-navy))] italic">
                  {result.new_customer_reply}
                </div>
                <button
                  type="button"
                  onClick={() => playText(result.new_customer_reply)}
                  className="wpe-ghost text-[11px] mt-1"
                  data-testid={`retry-play-customer-${index}`}
                >
                  <Play size={12} /> {tr("listen")}
                </button>
              </div>
              <div className="wpe-hint text-[12px]">
                <span className="wpe-eyebrow block mb-1">{tr("coachNote")}</span>
                {result.coach_note_es}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
